import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import {
  Search, Shield, Zap, Globe, BarChart3, FileText, ArrowRight, Loader2,
  CheckCircle, XCircle, AlertTriangle, Wrench, TrendingUp, Smartphone,
  Sparkles, ChevronRight, Info, ExternalLink, Trash2
} from 'lucide-react'
import { clsx } from 'clsx'
import { FileDown } from 'lucide-react'
import api from '../lib/api'
import { useAuthStore } from '../store/authStore'
import ScoreGauge from '../components/ScoreGauge'
import AnalysisCard from '../components/AnalysisCard'
import SubscriptionGate from '../components/SubscriptionGate'
import LatestPosts from '../components/LatestPosts'
import { isPaidPlan, buildAnalysisSections, downloadComprehensiveReport } from '../lib/reportPdf'
import { useSeoHead } from '../lib/seo'
import { useConfirm } from '../lib/useConfirm'
import { toast } from '../lib/toast'
import PromoBanner from '../components/PromoBanner'
import { useFocusTrap } from '../lib/useFocusTrap'
import Turnstile, { turnstileEnabled } from '../components/Turnstile'
import { featurePath } from '../lib/localizedUrl'

export default function HomePage() {
  const { t, i18n } = useTranslation()
  const { isAuthenticated, user } = useAuthStore()

  // Self-referencing canonical + hreflang per jazyk (/en → canonical /en, ne na /).
  // Title/description necháváme z kurátorovaného index.html.
  useSeoHead({ canonicalPath: '/' })
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState('')
  const [gateOpen, setGateOpen] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)

  const exportPdf = async () => {
    if (!result) return
    if (!isPaidPlan(user?.plan)) { setGateOpen(true); return }
    setPdfLoading(true)
    try {
      await downloadComprehensiveReport({
        title: t('home.seo_analysis', { defaultValue: 'SEO analýza' }),
        url: result.url || url.trim(),
        domain: result.domain || url.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, ''),
        score: typeof result.score === 'number' ? result.score : null,
        sections: buildAnalysisSections(result.results || {}, t),
      })
    } catch (e: any) {
      if (e?.response?.status === 403) setGateOpen(true)
    } finally { setPdfLoading(false) }
  }
  const [publishResult, setPublishResult] = useState(true)
  // Turnstile token pro anonymní analýzu (přihlášený jde na /analyze/save bez CAPTCHy).
  const [captcha, setCaptcha] = useState('')
  const [showPublishInfo, setShowPublishInfo] = useState(false)
  const publishInfoRef = useRef<HTMLDivElement>(null)
  useFocusTrap(publishInfoRef, () => setShowPublishInfo(false), showPublishInfo)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const runAnalysis = async (targetUrl: string, publish = publishResult) => {
    if (!targetUrl.trim()) return
    setLoading(true); setError(''); setResult(null)
    try {
      // Přihlášený uživatel (i free) → /analyze/save = uloží se do jeho Historie.
      // Nepřihlášený → veřejná /analyze (anonymní, limit dle IP).
      const endpoint = isAuthenticated ? '/analyze/save' : '/analyze'
      const { data } = await api.post(endpoint, {
        url: targetUrl.trim(),
        public: publish,
        ...(isAuthenticated ? {} : { turnstile_token: captcha }),
      })
      setResult(data)
    } catch (err: any) {
      setError(err.response?.data?.message || t('home.analyze_error'))
    } finally {
      setLoading(false)
    }
  }

  // Ověří, že vstup vypadá jako platná doména/URL (aspoň domena.tld)
  const isValidUrl = (input: string): boolean => {
    const host = input.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').split(/[/?#]/)[0]
    return /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(host)
  }

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim()) return
    if (!isValidUrl(url)) { setError(t('home.invalid_url')); return }
    setError('')
    runAnalysis(url)
  }

  // Auto-spuštění analýzy z ?url= (např. z popupu „Nová analýza" na dashboardu)
  useEffect(() => {
    const u = searchParams.get('url')
    if (u) {
      setUrl(u)
      runAnalysis(u)
      setSearchParams({}, { replace: true }) // ať se po refreshi nespustí znovu
      window.scrollTo({ top: 0 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const results = result?.results || {}
  const issues = Object.entries(results).filter(([, v]: [string, any]) => v.status !== 'pass')
  const passes = Object.entries(results).filter(([, v]: [string, any]) => v.status === 'pass')
  // Pořadí pro listování Předchozí/Další v popupu „Zjistit více"
  const navIds = [...issues, ...passes].map(([k]) => k)

  return (
    <div>
      {/* ===== PROMO AKCE ===== */}
      <PromoBanner />
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden mesh-gradient-hero min-h-[560px] flex items-center">
        {/* Animated orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-[120px] -top-48 -left-24 animate-float" />
          <div className="absolute w-[400px] h-[400px] bg-accent-500/15 rounded-full blur-[100px] top-20 -right-32 animate-float" style={{ animationDelay: '-2s' }} />
          <div className="absolute w-[300px] h-[300px] bg-violet-500/10 rounded-full blur-[80px] -bottom-20 left-1/3 animate-float" style={{ animationDelay: '-4s' }} />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '64px 64px' }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-4 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-accent-400" />
              <span className="text-xs font-medium text-gray-300 tracking-wide">{t('home.badge')}</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight"
          >
            {t('home.hero_title1')}
            <span className="block bg-gradient-to-r from-accent-400 via-primary-300 to-violet-400 bg-clip-text text-transparent">
              {t('home.hero_title2')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-300 mb-10 max-w-xl mx-auto leading-relaxed"
          >
            {t('home.hero_subtitle')}
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            onSubmit={handleAnalyze}
            className="max-w-2xl mx-auto"
          >
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-accent-500 to-violet-500 rounded-2xl opacity-30 blur-lg group-hover:opacity-50 transition-opacity" />
              <div className="relative flex gap-2 bg-white/[0.08] backdrop-blur-xl border border-white/[0.12] rounded-2xl p-2">
                <div className="flex-1 relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white" />
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="example.com"
                    aria-label={t('home.hero_input_label', { defaultValue: 'Adresa webu k analýze' })}
                    className="w-full pl-12 pr-4 py-3.5 bg-transparent text-white text-lg placeholder-white/70 focus:outline-none font-medium"
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !url.trim() || (!isAuthenticated && turnstileEnabled() && !captcha)}
                  className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-500/30 transition-all disabled:opacity-40 flex items-center gap-2 shrink-0"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> {t('home.analyzing')}</>
                  ) : (
                    <><Search className="w-5 h-5" /> {t('home.analyze_btn')}</>
                  )}
                </button>
              </div>
            </div>
            {/* Anti-bot ochrana anonymní analýzy (jen pro nepřihlášené; spící bez site key). */}
            {!isAuthenticated && <Turnstile onVerify={setCaptcha} theme="dark" className="flex justify-center mt-4" />}
          </motion.form>

          {/* Publish toggle */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-4 max-w-2xl mx-auto flex items-center justify-center gap-3"
          >
            <button
              type="button"
              role="switch"
              aria-checked={publishResult}
              aria-label={t('home.publish_toggle')}
              onClick={() => setPublishResult(!publishResult)}
              className={clsx(
                'relative w-11 h-6 rounded-full transition-colors',
                publishResult ? 'bg-emerald-500' : 'bg-gray-600'
              )}
            >
              <span className={clsx(
                'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm',
                publishResult && 'translate-x-5'
              )} />
            </button>
            <span className="text-sm text-gray-300">{t('home.publish_toggle')}</span>
            <button
              type="button"
              onClick={() => setShowPublishInfo(true)}
              aria-label={t('home.publish_info_aria', { defaultValue: 'Více o zveřejnění analýzy' })}
              className="text-gray-500 hover:text-gray-300 transition"
            >
              <Info className="w-4 h-4" />
            </button>
          </motion.div>

          {/* Publish info modal */}
          {showPublishInfo && (
            // Backdrop wrapper: click-outside-to-close (modal has a visible close button)
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowPublishInfo(false)}>
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <motion.div
                ref={publishInfoRef}
                role="dialog"
                aria-modal="true"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative bg-gray-950 border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <button onClick={() => setShowPublishInfo(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white transition">
                  <XCircle className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{t('home.publish_title')}</h3>
                </div>
                <ul className="space-y-3 mb-5 text-left">
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <div><p className="text-white font-medium">{t('home.benefit1_title')}</p><p className="text-gray-300 text-xs mt-0.5">{t('home.benefit1_desc')}</p></div>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <div><p className="text-white font-medium">{t('home.benefit2_title')}</p><p className="text-gray-300 text-xs mt-0.5">{t('home.benefit2_desc')}</p></div>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <div><p className="text-white font-medium">{t('home.benefit3_title')}</p><p className="text-gray-300 text-xs mt-0.5">{t('home.benefit3_desc')}</p></div>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <div><p className="text-white font-medium">{t('home.benefit4_title')}</p><p className="text-gray-300 text-xs mt-0.5">{t('home.benefit4_desc')}</p></div>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mb-4">{t('home.publish_optout')}</p>
                <button
                  onClick={() => setShowPublishInfo(false)}
                  className="w-full bg-emerald-600 text-white py-2.5 rounded-xl font-semibold hover:bg-emerald-500 transition text-sm"
                >
                  {t('home.understood')}
                </button>
              </motion.div>
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 bg-red-500/15 border border-red-500/20 text-red-300 px-6 py-3 rounded-xl max-w-2xl mx-auto text-sm"
            >
              {error}
            </motion.div>
          )}
        </div>
      </section>

      {/* ===== RESULTS ===== */}
      {result && (
        <section className="max-w-5xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {t('home.results_for')} <span className="text-primary-600">{result.domain}</span>
            </h2>
            <ScoreGauge score={result.score} size="lg" />
          </motion.div>

          <div className="flex justify-center gap-6 mb-10 text-sm">
            <span className="flex items-center gap-1.5 text-red-500 font-medium">
              <XCircle className="w-4 h-4" /> {t('analysis.issues_count', { count: issues.length })}
            </span>
            <span className="flex items-center gap-1.5 text-emerald-500 font-medium">
              <CheckCircle className="w-4 h-4" /> {t('analysis.passes_count', { count: passes.length })}
            </span>
            <span className="text-gray-400">{Object.keys(results).length} {t('home.checks')}</span>
          </div>

          <div className="flex justify-center mb-10">
            <button
              onClick={exportPdf}
              disabled={pdfLoading}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition disabled:opacity-60"
            >
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {t('analysis.export_pdf')}
            </button>
          </div>
          <SubscriptionGate open={gateOpen} onClose={() => setGateOpen(false)} feature={t('analysis.export_pdf')} />

          {issues.length > 0 && (
            <div className="mb-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> {t('common.to_fix')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {issues.map(([key, value]: [string, any], i) => (
                  <motion.div key={key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <AnalysisCard checkKey={key} navIds={navIds} title={t(`checks.${key}`, { defaultValue: key })} result={value} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {passes.length > 0 && (
            <div className="mb-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" /> {t('common.all_ok')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {passes.map(([key, value]: [string, any], i) => (
                  <motion.div key={key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <AnalysisCard checkKey={key} navIds={navIds} title={t(`checks.${key}`, { defaultValue: key })} result={value} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center bg-gradient-to-r from-primary-50 to-accent-400/10 rounded-2xl p-8 border border-primary-100/50"
          >
            <p className="text-gray-700 mb-2 font-semibold">{t('home.want_more')}</p>
            <p className="text-gray-500 text-sm mb-6">{t('home.want_more_desc')}</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition-all inline-flex items-center gap-2">
                {t('home.register_free')} <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/tools"
                className="bg-white text-primary-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 transition border border-gray-200 inline-flex items-center gap-2">
                <Wrench className="w-4 h-4" /> {t('nav.tools')}
              </Link>
            </div>
          </motion.div>
        </section>
      )}

      {/* ===== FEATURES ===== */}
      {!result && (
        <section className="max-w-6xl mx-auto px-4 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              {t('home.features_title')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              {t('home.features_desc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Shield, titleKey: 'home.f1_title', descKey: 'home.f1_desc', color: 'from-emerald-500 to-teal-500', slug: 'bezpecnost-https' },
              { icon: FileText, titleKey: 'home.f2_title', descKey: 'home.f2_desc', color: 'from-primary-500 to-indigo-500', slug: 'on-page-seo' },
              { icon: Zap, titleKey: 'home.f3_title', descKey: 'home.f3_desc', color: 'from-amber-500 to-orange-500', slug: 'vykon-rychlost' },
              { icon: Globe, titleKey: 'home.f4_title', descKey: 'home.f4_desc', color: 'from-cyan-500 to-blue-500', slug: 'crawl-webu' },
              { icon: TrendingUp, titleKey: 'home.f5_title', descKey: 'home.f5_desc', color: 'from-violet-500 to-purple-500', slug: 'sledovani-pozic' },
              { icon: BarChart3, titleKey: 'home.f6_title', descKey: 'home.f6_desc', color: 'from-pink-500 to-rose-500', slug: 'obsah-klicova-slova' },
              { icon: Smartphone, titleKey: 'home.f7_title', descKey: 'home.f7_desc', color: 'from-sky-500 to-cyan-500', slug: 'pristupnost' },
              { icon: Search, titleKey: 'home.f8_title', descKey: 'home.f8_desc', color: 'from-fuchsia-500 to-pink-500', slug: 'srovnani-konkurence' },
              { icon: Wrench, titleKey: 'home.f9_title', descKey: 'home.f9_desc', color: 'from-lime-500 to-emerald-500', slug: 'seo-nastroje' },
            ].map(({ icon: Icon, titleKey, descKey, color, slug }, i) => (
              <motion.div
                key={titleKey}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="group relative bg-white rounded-2xl p-6 border border-gray-100 hover:border-gray-200 transition-all hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1"
              >
                <div className={clsx('w-11 h-11 rounded-xl bg-gradient-to-br flex items-center justify-center mb-4 shadow-lg', color)}
                  style={{ boxShadow: `0 8px 24px -4px rgba(0,0,0,0.15)` }}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1.5 text-[15px]">{t(titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(descKey)}</p>
                <ChevronRight className="absolute top-6 right-5 w-4 h-4 text-gray-300 group-hover:text-primary-400 group-hover:translate-x-0.5 transition-all" />
                <Link to={featurePath(slug, i18n.language)} className="absolute inset-0" aria-label={t(titleKey)} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link
              to="/tools"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-10 py-4 rounded-2xl text-lg font-semibold hover:shadow-xl hover:shadow-primary-500/25 transition-all"
            >
              {t('home.try_free')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </section>
      )}

      {/* ===== NEJNOVĚJŠÍ Z BLOGU ===== */}
      {!result && <LatestPosts />}

      {/* ===== RECENT ANALYSES ===== */}
      {!result && <RecentAnalyses />}
    </div>
  )
}

function RecentAnalyses() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()
  const { data } = useQuery({
    queryKey: ['recent-public'],
    queryFn: () => api.get('/analyses/recent?limit=12').then((r) => r.data.analyses),
  })

  // Homepage výpis je seskupený podle domény → odebíráme celou doménu (ne jednu analýzu),
  // jinak by se po smazání jen odkryla předchozí analýza téhož webu a dlaždice by zůstala.
  const del = useMutation({
    mutationFn: (domain: string) => api.delete(`/analyses/public/domain/${encodeURIComponent(domain)}`),
    onSuccess: () => { toast.success(t('home.recent_deleted', { defaultValue: 'Analýza smazána' })); qc.invalidateQueries({ queryKey: ['recent-public'] }) },
    onError: () => toast.error(t('common.error', { defaultValue: 'Smazání selhalo' })),
  })

  const handleDelete = async (e: React.MouseEvent, domain: string) => {
    e.preventDefault(); e.stopPropagation()
    if (await confirm({ title: t('home.recent_delete_title', { defaultValue: 'Smazat analýzu?' }), message: t('home.recent_delete_msg', { defaultValue: 'Analýza zmizí z veřejného seznamu na hlavní stránce.' }), confirmText: t('common.delete', { defaultValue: 'Smazat' }) })) {
      del.mutate(domain)
    }
  }

  if (!data?.length) return null

  return (
    <section className="max-w-6xl mx-auto px-4 pb-20">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center tracking-tight">{t('home.recent')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((a: any) => (
            <motion.article
              key={a.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all group flex flex-col"
            >
              {/* Náhled → interní stránka s analýzou (náš funnel) */}
              <Link to={`/results/${a.domain}`} className="block relative overflow-hidden">
                <img
                  src={`${api.defaults.baseURL}/analyses/screenshot?url=https://${a.domain}&w=450&h=300`}
                  alt={`${a.title || a.domain} – náhled webu`}
                  className="w-full aspect-[3/2] object-cover object-top bg-gray-100 transition-transform duration-300 group-hover:scale-[1.03]"
                  loading="lazy"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
                <span className={clsx('absolute top-2 right-2 w-9 h-9 rounded-lg flex items-center justify-center text-sm font-extrabold text-white shadow-md',
                  a.score >= 80 ? 'bg-emerald-500' : a.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                )}>{a.score}</span>
                {isAdmin && (
                  <button
                    onClick={(e) => handleDelete(e, a.domain)}
                    title={t('home.recent_delete_title', { defaultValue: 'Smazat analýzu?' })}
                    className="absolute top-2 left-2 w-9 h-9 rounded-lg bg-red-500/90 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </Link>

              <div className="p-4 flex flex-col flex-1">
                {/* Titulek = skutečný zpětný odkaz na analyzovaný web (anchor text = title) */}
                <h3 className="text-sm font-semibold leading-snug">
                  <a
                    href={`https://${a.domain}`}
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-start gap-1 text-gray-900 hover:text-primary-600 transition-colors line-clamp-2"
                  >
                    <span className="line-clamp-2">{a.title || a.domain}</span>
                    <ExternalLink className="w-3 h-3 mt-[3px] shrink-0 text-gray-300 group-hover:text-primary-400 transition-colors" />
                  </a>
                </h3>
                <span className="block text-[11px] text-gray-400 truncate mt-0.5">{a.domain}</span>

                {a.description && (
                  <p className="text-xs text-gray-500 leading-relaxed mt-2 line-clamp-2">{a.description}</p>
                )}

                <div className="mt-auto pt-3">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div
                      className={clsx('h-1.5 rounded-full transition-all',
                        a.score >= 80 ? 'bg-emerald-500' : a.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      )}
                      style={{ width: `${a.score}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-gray-400">{new Date(a.created_at).toLocaleDateString('cs-CZ')}</span>
                    <Link to={`/results/${a.domain}`} className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center gap-0.5">
                      {t('home.recent_view')} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/nedavne-analyzy" className="inline-flex items-center gap-2 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 px-6 py-2.5 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-white/5 transition">
            {t('home.recent_view_all')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </motion.div>
      <ConfirmDialog />
    </section>
  )
}
